<div
  class="box-border flex w-full flex-col bg-inherit text-base"
  [ngClass]="{
    'cursor-not-allowed opacity-60': ngControl.control!.disabled,
    'bg-primary-on': !corporateDesignService.isDarkModeActive(),
  }"
  [ngStyle]="{
    background: inputBackground(),
  }">
  @let hasError = ngControl.errors && ngControl.touched;
  <div class="relative w-full bg-inherit" [id]="id() | appendTestId: 'anchor'">
    <div
      class="focus-within:border-input-focus relative flex w-full items-center bg-transparent text-left"
      [ngClass]="{
        'border-input rounded-input p-input min-h-11':
          appearance() === 'default',
        'bg-primary-on min-h-10 rounded-md border px-3 py-1 text-sm font-normal':
          appearance() === 'compact',
        'border-input-error': hasError,
        'border-input-ring': !hasError && appearance() === 'default',
        'border-standard-outline-light':
          !hasError && appearance() === 'compact',
      }"
      [ngStyle]="{
        borderRadius: borderRounding(),
        borderColor: borderAndLabelColor(),
        color: corporateDesignService.fontColor(),
      }">
      <button
        type="button"
        aria-haspopup="listbox"
        [attr.aria-expanded]="showList()"
        [attr.aria-label]="
          selectedOptionLabels() || placeholder() || 'Auswählen'
        "
        [attr.data-testid]="id() | appendTestId: 'input'"
        [disabled]="ngControl.control!.disabled || isLoading()"
        [id]="id()"
        class="absolute inset-0 rounded-[inherit] focus:ring-0 focus:outline-none"
        (click)="toggleList()"
        (focus)="isFocused.set(true)"
        (blur)="isFocused.set(false)"
        (keydown)="handleKeydown($event)"></button>

      <div
        class="pointer-events-none relative flex min-w-0 flex-1 flex-wrap items-center gap-1">
        @for (option of selectedOptions(); track $index) {
          <span
            class="border-primary-main bg-base-tonal text-primary-main flex max-w-full items-center rounded-full border px-3 text-base leading-none"
            [attr.data-testid]="id() | appendTestId: 'chip-' + $index">
            <span class="truncate">
              {{ optionLabel(option) }}
            </span>
            <button
              type="button"
              class="pointer-events-auto ml-1 flex shrink-0 cursor-pointer items-center justify-center"
              [disabled]="ngControl.control!.disabled || isLoading()"
              [attr.aria-label]="optionLabel(option) + ' entfernen'"
              [attr.data-testid]="id() | appendTestId: 'remove-chip-' + $index"
              (click)="removeSelectedOption(option, $event)">
              <span
                class="material-symbols-rounded text-2xl!"
                aria-hidden="true">
                close
              </span>
            </button>
          </span>
        } @empty {
          <span class="truncate">
            {{ placeholder() || 'Auswählen' }}
          </span>
        }
      </div>

      @if (isLoading()) {
        <span
          class="material-symbols-rounded pointer-events-none relative ml-2 animate-spin"
          aria-hidden="true">
          progress_activity
        </span>
      } @else {
        <span
          class="material-symbols-rounded pointer-events-none relative ml-2"
          aria-hidden="true">
          {{ showList() ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
        </span>
      }
    </div>

    @if (label()) {
      <label
        [attr.data-testid]="id() | appendTestId: 'label'"
        class="pointer-events-none absolute -top-2 left-4 bg-inherit px-1 text-sm"
        [ngClass]="{
          'text-input-error': hasError,
          'text-input-focus': isFocused(),
        }"
        [ngStyle]="{
          color: borderAndLabelColor(),
        }"
        [for]="id()">
        {{ formattedLabel() }}
      </label>
    }

    <div
      awPopupPosition
      role="listbox"
      aria-multiselectable="true"
      tabindex="-1"
      [anchor]="id() | appendTestId: 'anchor'"
      [isVisible]="showList()"
      [attr.data-testid]="id() | appendTestId: 'options'"
      [ngClass]="{
        hidden: !showList(),
        'bg-primary-on': !corporateDesignService.isDarkModeActive(),
        'bg-input-ring': corporateDesignService.isDarkModeActive(),
      }"
      [ngStyle]="{
        borderRadius: borderRounding(),
      }"
      class="border-input-ring border-input rounded-input fixed z-10 -mt-px max-h-[300px] w-full overflow-y-auto">
      @if (searchable()) {
        <div class="sticky top-0 z-10 flex items-center bg-inherit px-3 py-2">
          <input
            [attr.data-bwignore]="ignoreBitwardenAutofill ? 'true' : null"
            type="checkbox"
            aria-label="Alle auswählen"
            [checked]="areAllOptionsSelected()"
            [indeterminate]="areSomeOptionsSelected()"
            [attr.data-testid]="id() | appendTestId: 'select-all'"
            class="focus:border-input-focus border-input-ring text-input-focus border-input h-5 w-5 shrink-0 cursor-pointer rounded-sm focus:ring-0 focus:ring-offset-0 focus:outline-none"
            [ngClass]="{
              'border-input-focus border-2': areAllOptionsSelected(),
              'text-input-error! border-input-error!': hasError,
            }"
            [ngStyle]="{
              color: inputSelectedColor(),
              borderRadius: borderRounding(),
              borderColor: hasError
                ? ''
                : corporateDesignService.primaryColor(),
            }"
            (change)="toggleAllOptions()" />
          <div class="relative ml-2 min-w-0 flex-1">
            <span
              class="material-symbols-rounded text-input-label pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
              aria-hidden="true">
              search
            </span>
            <input
              [attr.data-bwignore]="ignoreBitwardenAutofill ? 'true' : null"
              type="search"
              autocomplete="off"
              [formControl]="searchControl"
              [attr.data-testid]="id() | appendTestId: 'search'"
              [placeholder]="searchPlaceholder()"
              class="focus:border-input-focus border-input-ring border-input rounded-input h-10 w-full bg-transparent pr-3 pl-10 focus:outline-none"
              (keydown)="handleKeydown($event)" />
          </div>
        </div>
      }

      @for (option of filteredOptions(); track $index; let index = $index) {
        @let isSelected = isOptionSelected(option);
        <label
          role="option"
          [attr.aria-selected]="isSelected"
          class="hover:bg-standard-hover-main flex min-h-10 cursor-pointer items-center px-3 py-2"
          [ngClass]="{
            'bg-standard-hover-main': selectedItem() === index,
          }"
          [attr.data-testid]="id() + index | appendTestId: 'label'"
          (mouseenter)="selectedItem.set(index)"
          (click)="selectOption(option); $event.preventDefault()">
          <input
            [attr.data-bwignore]="ignoreBitwardenAutofill ? 'true' : null"
            type="checkbox"
            [id]="id() | appendTestId: index + ''"
            [name]="name() ? name()! + index : undefined"
            [checked]="isSelected"
            [attr.data-testid]="id() + index | appendTestId: 'input'"
            class="focus:border-input-focus border-input-ring text-input-focus border-input h-5 w-5 cursor-pointer focus:ring-0 focus:ring-offset-0 focus:outline-none"
            [ngClass]="{
              'border-input-focus border-2': isSelected,
              'text-input-error! border-input-error!': hasError,
            }"
            [ngStyle]="{
              color: inputSelectedColor(),
              borderRadius: borderRounding(),
              borderColor: hasError
                ? ''
                : corporateDesignService.primaryColor(),
            }" />
          <span class="pl-2">
            {{ option | toSelectOptionAttribute: name() }}
          </span>
        </label>
      }

      @if (filteredOptions().length === 0) {
        <div class="px-3 py-2">
          {{
            textStore.texts()
              | filterTexts: ERelationType.noOptions : EEntityType.generic
              | language
          }}
        </div>
      }
    </div>
  </div>

  @if (hasError && !errorTemplate()) {
    <aw-error-message [attr.data-testid]="id() | appendTestId: 'error'">
      <ng-content></ng-content>
    </aw-error-message>
  }
  @if (hasError && errorTemplate()) {
    <aw-error-message [attr.data-testid]="id() | appendTestId: 'error'">
      <ng-container *ngTemplateOutlet="errorTemplate()!"></ng-container>
    </aw-error-message>
  }
</div>
